/**
 * Utility function to safely open email client
 * Handles iOS scrolling issues and provides fallback
 */
export const contactUs = () => {
  // iOS scrolling fix - prevent any scroll adjustments for a moment
  const isIOS =
    /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;

  const email = 'contact@furnitech.app';
  const subject = 'Furnitech Consultation Request';
  const body =
    "Hello,\n\nI am interested in learning more about Furnitech's interior design services.\n\nThank you!";

  const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  if (isIOS) {
    // For iOS, slightly delay the email opening to prevent scroll issues
    setTimeout(() => {
      window.location.href = mailtoUrl;
    }, 50);
  } else {
    window.location.href = mailtoUrl;
  }
};

/**
 * Debounce function to limit the rate of function execution
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @param {boolean} immediate - Whether to execute immediately
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait, immediate) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

/**
 * Throttle function to limit function execution frequency
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Check if element is in viewport
 * @param {Element} element - DOM element to check
 * @returns {boolean} Whether element is visible
 */
export const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

/**
 * Smooth scroll to element with offset
 * @param {string} elementId - ID of target element
 * @param {number} offset - Offset from top in pixels
 */
export const smoothScrollTo = (elementId, offset = 0) => {
  const element = document.getElementById(elementId);
  if (!element) return;

  const isIOS =
    /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
  const yPosition =
    element.getBoundingClientRect().top + window.scrollY + offset;

  if (isIOS) {
    // Use auto scrolling for iOS to prevent issues
    window.scrollTo({
      top: yPosition,
      behavior: 'auto',
    });
  } else {
    window.scrollTo({
      top: yPosition,
      behavior: 'smooth',
    });
  }
};
